import { useEffect, useMemo, useState } from 'react';
import { Marker, Tooltip, useMap } from 'react-leaflet';
import L from 'leaflet';
import { supabase } from '../lib/supabase.js';
import { KEY_RIVERS, RIVER_LABELS, MOUNTAIN_RANGES } from '../data/geoFeatures.js';
import { riverLabelPlacement } from '../utils/riverLabelPlacement.js';
import riverLines from '../data/riverLines.json';

// App-side markers for the Key Rivers & Mountains layer: mountain icon
// spreads + range labels, and per-river labels rotated in line with the
// river. River label placements live in geo_river_labels (any number per
// river — the tool clones labels along the course); rivers without rows
// fall back to the computed centerline placement. The GL line labels
// (z9+) are separate and not editable.

const MTN_SVG =
  '<svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true">'
  + '<path d="M8.3 9.8 13.2 19.5H2.5L8.3 9.8z" opacity="0.45" />'
  + '<path d="M14.6 5 21.8 19.5H7.9L14.6 5z" /></svg>';

const mtnIcon = () => L.divIcon({
  className: 'geo-mtn-icon',
  html: MTN_SVG,
  iconSize: [22, 22],
  iconAnchor: [11, 11],
});

const labelIcon = (name, cls, angle = 0, toolsHtml = '') => L.divIcon({
  className: 'geo-label-wrap',
  html: `<span class="geo-label ${cls}" style="transform:translate(-50%,-50%) rotate(${angle}deg)">${name}</span>${toolsHtml}`,
  iconSize: [0, 0],
  iconAnchor: [0, 0],
});

const normAngle = (a) => {
  let n = a;
  if (n > 90) n -= 180;
  if (n < -90) n += 180;
  return Math.round(n) || 0;
};

// ── Screen-space collision helpers ───────────────────────────────────────
const LABEL_FONT = 12.5;
const MTN_LABEL_FONT = 11.5;
const intersects = (a, b) => (
  a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y
);

// axis-aligned bbox of the (possibly rotated) river label
const riverLabelRect = (map, label) => {
  const pt = map.latLngToContainerPoint([label.latitude, label.longitude]);
  const w0 = label.river.length * LABEL_FONT * 0.62;
  const h0 = LABEL_FONT * 1.3;
  const rad = (Math.abs(label.angle ?? 0) * Math.PI) / 180;
  const w = w0 * Math.cos(rad) + h0 * Math.sin(rad);
  const h = w0 * Math.sin(rad) + h0 * Math.cos(rad);
  return { x: pt.x - w / 2, y: pt.y - h / 2, w, h };
};

// first in row order wins (stored labels come before computed defaults)
const cullRiverLabels = (map, labels) => {
  const rects = [];
  return labels.map((label) => {
    const rect = riverLabelRect(map, label);
    const visible = !rects.some((r) => intersects(rect, r));
    if (visible) rects.push(rect);
    return { label, rect, visible };
  });
};

// Everything the geo layer draws, as screen rects — RegionLabels hides
// any region label colliding with these in the geo view. Kept current by
// RiverLabels/MountainIcons (module state) since both are DB-backed.
let _currentRiverLabels = [];
let _mtnOverrides = new Map(); // "range/idx" -> [lat, lng]
const mtnIconPos = (range, i) => _mtnOverrides.get(`${range.name}/${i}`) ?? range.icons[i];
export const geoObstacleRects = (map) => {
  const rects = cullRiverLabels(map, _currentRiverLabels)
    .filter((c) => c.visible)
    .map((c) => c.rect);
  for (const range of MOUNTAIN_RANGES) {
    range.icons.forEach((_, i) => {
      const [lat, lng] = mtnIconPos(range, i);
      const pt = map.latLngToContainerPoint([lat, lng]);
      rects.push({ x: pt.x - 11, y: pt.y - 11, w: 22, h: 22 });
    });
    for (const p of range.peaks ?? []) {
      const pt = map.latLngToContainerPoint([p.lat, p.lng]);
      rects.push({ x: pt.x - 11, y: pt.y - 11, w: 22, h: 22 });
    }
    const lp = map.latLngToContainerPoint(range.label);
    const w = range.name.length * MTN_LABEL_FONT * 0.62;
    const h = MTN_LABEL_FONT * 1.3;
    rects.push({ x: lp.x - w / 2, y: lp.y - h / 2, w, h });
  }
  return rects;
};

// ── Mountain icons: hand-placed spread, per-icon DB overrides ────────────
let _mtnRows = null;
const fetchMtnRows = () => {
  _mtnRows ??= supabase
    .from('geo_mountain_icons')
    .select('range,idx,latitude,longitude')
    .then(({ data, error }) => {
      if (error) { _mtnRows = null; throw error; }
      return data ?? [];
    });
  return _mtnRows;
};

function MountainIcons({ edit, selected, onSelect }) {
  const map = useMap();
  const [rows, setRows] = useState(null);
  useEffect(() => {
    let alive = true;
    fetchMtnRows()
      .then((r) => { if (alive) setRows(r); })
      .catch(() => { if (alive) setRows([]); });
    return () => { alive = false; };
  }, []);

  const overrides = useMemo(() => new Map(
    (rows ?? []).map((r) => [`${r.range}/${r.idx}`, [r.latitude, r.longitude]])
  ), [rows]);
  useEffect(() => {
    _mtnOverrides = overrides;
    map.fire('geo-obstacles');
    return () => { _mtnOverrides = new Map(); };
  }, [overrides, map]);

  const toast = (latlng, ok, msg) => {
    const t = L.marker(latlng, {
      interactive: false, pane: 'geo-markers',
      icon: L.divIcon({ className: `village-toast village-toast--${ok ? 'ok' : 'err'}`, html: msg, iconSize: null, iconAnchor: [-10, -10] }),
    });
    t.addTo(map);
    setTimeout(() => map.removeLayer(t), ok ? 1600 : 3000);
  };

  const save = async (rangeName, idx, latlng, marker, prev) => {
    const { error } = await supabase.rpc('upsert_geo_mountain_icon', {
      p_range: rangeName, p_idx: idx, p_latitude: latlng.lat, p_longitude: latlng.lng,
    });
    if (error) {
      marker.setLatLng(prev);
      toast(L.latLng(prev), false, error.message ?? 'Save failed');
      return;
    }
    _mtnRows = null;
    setRows((cur) => {
      const rest = (cur ?? []).filter((r) => !(r.range === rangeName && r.idx === idx));
      return [...rest, { range: rangeName, idx, latitude: latlng.lat, longitude: latlng.lng }];
    });
    toast(latlng, true, '&#10003; Saved');
  };

  return MOUNTAIN_RANGES.flatMap((range) => {
    const dim = !!selected && !(selected.kind === 'mountain' && selected.name === range.name);
    const spread = range.icons.map((_, i) => {
      const [lat, lng] = mtnIconPosFrom(overrides, range, i);
      return (
        <Marker
          key={`m-${range.name}-${i}`}
          position={[lat, lng]}
          icon={mtnIcon()}
          pane="geo-markers"
          opacity={dim ? 0.3 : 1}
          draggable={edit}
          eventHandlers={{
            ...(edit
              ? { dragend: (e) => save(range.name, i, e.target.getLatLng(), e.target, [lat, lng]) }
              : { click: () => onSelect({ kind: 'mountain', name: range.name }) }),
          }}
        />
      );
    });
    // the range's named summits: fixed on the real coordinates (never
    // draggable — they're geography, not layout), with a name/elevation
    // tooltip; clicking opens the range like any other icon
    const peaks = (range.peaks ?? []).map((p) => (
      <Marker
        key={`pk-${range.name}-${p.name}`}
        position={[p.lat, p.lng]}
        icon={mtnIcon()}
        pane="geo-markers"
        opacity={dim ? 0.3 : 1}
        draggable={false}
        eventHandlers={{ click: () => onSelect({ kind: 'mountain', name: range.name }) }}
      >
        <Tooltip direction="top" offset={[0, -8]} className="heli-tooltip" opacity={1}>
          {`${p.name} | ${p.ele} m`}
        </Tooltip>
      </Marker>
    ));
    return [...spread, ...peaks];
  });
}
const mtnIconPosFrom = (overrides, range, i) => overrides.get(`${range.name}/${i}`) ?? range.icons[i];

// ── River labels: DB-backed, editable ────────────────────────────────────
let _riverLabelRows = null;
const fetchRiverLabelRows = () => {
  _riverLabelRows ??= supabase
    .from('geo_river_labels')
    .select('id,river,latitude,longitude,angle')
    .then(({ data, error }) => {
      if (error) { _riverLabelRows = null; throw error; }
      return data ?? [];
    });
  return _riverLabelRows;
};

const defaultLabelFor = (name) => {
  const anchor = RIVER_LABELS.find((r) => r.name === name);
  const geom = riverLines.features.find((f) => f.properties.name === name)?.geometry.coordinates;
  const { lat, lng, angle } = riverLabelPlacement(geom, anchor);
  return { id: null, river: name, latitude: lat, longitude: lng, angle };
};

function RiverLabels({ edit, selected, onSelect }) {
  const map = useMap();
  const [rows, setRows] = useState(null);
  useEffect(() => {
    let alive = true;
    fetchRiverLabelRows()
      .then((r) => { if (alive) setRows(r); })
      .catch(() => { if (alive) setRows([]); });
    return () => { alive = false; };
  }, []);

  // every river always has at least one label: stored rows, else default
  const labels = useMemo(() => {
    if (!rows) return [];
    const have = new Set(rows.map((r) => r.river));
    return [...rows, ...KEY_RIVERS.filter((n) => !have.has(n)).map(defaultLabelFor)];
  }, [rows]);

  useEffect(() => {
    if (!labels.length) return undefined;
    const group = L.layerGroup();
    const placed = [];

    // insert (id null) or update; syncs local state and drops the module
    // cache so the next mount refetches
    const saveRow = async (label, patch) => {
      const next = { ...label, ...patch };
      const { data, error } = await supabase.rpc('upsert_geo_river_label', {
        p_id: label.id, p_river: label.river,
        p_latitude: next.latitude, p_longitude: next.longitude, p_angle: next.angle,
      });
      if (error) throw error;
      _riverLabelRows = null;
      const id = data ?? label.id;
      setRows((prev) => {
        const cur = prev ?? [];
        return label.id == null
          ? [...cur, { ...next, id }]
          : cur.map((r) => (r.id === label.id ? { ...next, id } : r));
      });
    };

    const toast = (latlng, ok, msg) => {
      const t = L.marker(latlng, {
        interactive: false, pane: 'geo-markers',
        icon: L.divIcon({ className: `village-toast village-toast--${ok ? 'ok' : 'err'}`, html: msg, iconSize: null, iconAnchor: [-10, -10] }),
      });
      group.addLayer(t);
      setTimeout(() => group.removeLayer(t), ok ? 1600 : 3000);
    };

    for (const label of labels) {
      const dim = !!selected && !(selected.kind === 'river' && selected.name === label.river);
      const toolsHtml = edit
        ? '<span class="geo-rlt-row">'
          + '<button type="button" class="geo-rlt geo-rlt--rot" title="Drag to rotate">&#10227;</button>'
          + '<button type="button" class="geo-rlt geo-rlt--add" title="Add another label for this river">+</button>'
          + (label.id != null ? '<button type="button" class="geo-rlt geo-rlt--del" title="Remove this label">&times;</button>' : '')
          + '</span>'
        : '';
      const marker = L.marker([label.latitude, label.longitude], {
        pane: 'geo-markers',
        zIndexOffset: 1000,
        interactive: true,
        draggable: edit,
        opacity: dim ? 0.3 : 1,
        icon: labelIcon(label.river, 'geo-label--river', label.angle, toolsHtml),
      });

      if (!edit) {
        marker.on('click', () => onSelect({ kind: 'river', name: label.river }));
      } else {
        marker.on('dragend', () => {
          const p = marker.getLatLng();
          saveRow(label, { latitude: p.lat, longitude: p.lng })
            .then(() => toast(p, true, '&#10003; Saved'))
            .catch((e) => {
              marker.setLatLng([label.latitude, label.longitude]);
              toast(marker.getLatLng(), false, e.message ?? 'Save failed');
            });
        });
        marker.on('add', () => {
          const el = marker.getElement();
          if (!el) return;
          const span = el.querySelector('.geo-label');
          const stopAll = (btn) => L.DomEvent.on(btn, 'mousedown dblclick', L.DomEvent.stop);

          const rot = el.querySelector('.geo-rlt--rot');
          if (rot) {
            L.DomEvent.on(rot, 'mousedown', (e) => {
              L.DomEvent.stop(e);
              let latest = null;
              const move = (ev) => {
                const pt = map.mouseEventToContainerPoint(ev);
                const c = map.latLngToContainerPoint(marker.getLatLng());
                latest = normAngle((Math.atan2(pt.y - c.y, pt.x - c.x) * 180) / Math.PI);
                span.style.transform = `translate(-50%,-50%) rotate(${latest}deg)`;
              };
              const up = () => {
                document.removeEventListener('mousemove', move);
                document.removeEventListener('mouseup', up);
                if (latest != null) {
                  saveRow(label, { angle: latest })
                    .then(() => toast(marker.getLatLng(), true, '&#10003; Saved'))
                    .catch((err) => toast(marker.getLatLng(), false, err.message ?? 'Save failed'));
                }
              };
              document.addEventListener('mousemove', move);
              document.addEventListener('mouseup', up);
            });
          }

          const add = el.querySelector('.geo-rlt--add');
          if (add) {
            stopAll(add);
            L.DomEvent.on(add, 'click', (e) => {
              L.DomEvent.stop(e);
              // clone lands a step down-right so it never hides the original
              const c = map.latLngToContainerPoint(marker.getLatLng());
              const p2 = map.containerPointToLatLng([c.x + 46, c.y + 26]);
              saveRow({ id: null, river: label.river, latitude: p2.lat, longitude: p2.lng, angle: label.angle }, {})
                .catch((err) => toast(marker.getLatLng(), false, err.message ?? 'Save failed'));
            });
          }

          const del = el.querySelector('.geo-rlt--del');
          if (del) {
            stopAll(del);
            L.DomEvent.on(del, 'click', async (e) => {
              L.DomEvent.stop(e);
              const { error } = await supabase.rpc('delete_geo_river_label', { p_id: label.id });
              if (error) { toast(marker.getLatLng(), false, error.message); return; }
              _riverLabelRows = null;
              setRows((prev) => (prev ?? []).filter((r) => r.id !== label.id));
            });
          }
        });
      }
      group.addLayer(marker);
      placed.push({ marker, label });
    }

    // Collision culling: labels that would overlap on screen hide, first
    // in row order wins (stored labels beat computed defaults by coming
    // first). Edit mode shows everything — a hidden label could never be
    // repositioned out of its collision. The custom event tells
    // RegionLabels the geo obstacles changed.
    _currentRiverLabels = labels;
    const updateVisibility = () => {
      if (!edit) {
        cullRiverLabels(map, labels).forEach(({ visible }, i) => {
          if (visible) group.addLayer(placed[i].marker);
          else group.removeLayer(placed[i].marker);
        });
      }
      map.fire('geo-obstacles');
    };
    updateVisibility();
    map.on('zoomend moveend', updateVisibility);

    group.addTo(map);
    return () => {
      map.off('zoomend moveend', updateVisibility);
      map.removeLayer(group);
      _currentRiverLabels = [];
    };
  }, [labels, edit, map, selected, onSelect]);

  return null;
}

export default function GeoMarkers({ onSelect, selected, riverEdit, mtnEdit }) {
  // range labels only — the icons render (and drag) in MountainIcons
  const markers = useMemo(() => MOUNTAIN_RANGES.map((range) => ({
    key: `ml-${range.name}`, lat: range.label[0], lng: range.label[1],
    icon: labelIcon(range.name, 'geo-label--mtn'), isLabel: true,
    select: { kind: 'mountain', name: range.name },
  })), []);

  // Any selection dims everything except the selected feature — same
  // kind and the other kind alike.
  const dimmed = (m) => (
    !!selected && !(selected.kind === m.select.kind && selected.name === m.select.name)
  );
  const render = (m) => (
    <Marker
      key={m.key}
      position={[m.lat, m.lng]}
      icon={m.icon}
      pane="geo-markers"
      opacity={dimmed(m) ? 0.3 : 1}
      // labels ride above the mountain icons (same pane, offset wins
      // over Leaflet's latitude-based stacking)
      zIndexOffset={m.isLabel ? 1000 : 0}
      eventHandlers={{ click: () => onSelect(m.select) }}
    />
  );
  return (
    <>
      {markers.map(render)}
      <MountainIcons edit={mtnEdit} selected={selected} onSelect={onSelect} />
      <RiverLabels edit={riverEdit} selected={selected} onSelect={onSelect} />
    </>
  );
}
